import { isAxiosError } from 'axios';
import { NetworkReportScope } from './networkReportScope';
import { shouldCaptureApiError } from './sentryCapture';

interface RequestRecord {
  errors: Set<object>;
  groups: Set<PromiseGroup>;
  independentConsumer: boolean;
}

interface PromiseGroup {
  scope: NetworkReportScope;
  members: Set<RequestRecord>;
  fulfilled: Set<object>;
  valid: boolean;
  owner?: object;
}

const promises = new WeakSet<object>();
const rejectionGroups = new WeakMap<object, PromiseGroup>();
const errorRequests = new WeakMap<object, Set<RequestRecord>>();

export function hasIndependentNetworkConsumer(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  return [...(errorRequests.get(error) ?? [])].some((request) => request.independentConsumer);
}

function nativeCallback(callback: unknown): callback is object {
  return (
    typeof callback === 'function' &&
    callback.name === '' &&
    callback.length === 1 &&
    Function.prototype.toString.call(callback).includes('[native code]')
  );
}

function recordGroupError(group: PromiseGroup, error: object): void {
  if (
    isAxiosError(error) &&
    !error.response &&
    !error.config?._sentryReportScope &&
    shouldCaptureApiError(error)
  )
    group.scope.record(error);
}

function joinGroup(record: RequestRecord, fulfilled: object, rejected: object): void {
  let group = rejectionGroups.get(rejected);
  if (!group) {
    group = {
      scope: new NetworkReportScope(),
      members: new Set(),
      fulfilled: new Set(),
      valid: true,
    };
    rejectionGroups.set(rejected, group);
  }
  // PerformPromiseAll shares reject but creates a different resolve element per input.
  // A shared resolve is Promise.race, whose losing requests must retain their reports.
  if (group.fulfilled.has(fulfilled)) group.valid = false;
  group.fulfilled.add(fulfilled);
  group.members.add(record);
  record.groups.add(group);
  for (const error of record.errors) recordGroupError(group, error);
}

export function networkPromiseGroup(
  error: unknown,
  owner?: object
): NetworkReportScope | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const requests = errorRequests.get(error);
  if (!requests || requests.size !== 1) return undefined;
  const [request] = requests;
  if (!request || request.groups.size !== 1) return undefined;
  const [group] = request.groups;
  if (!group?.valid || group.members.size < 2) return undefined;
  for (const member of group.members) {
    if (member.independentConsumer || member.groups.size !== 1) return undefined;
  }
  if (owner) {
    if (group.owner && group.owner !== owner) return undefined;
    group.owner = owner;
  }
  return group.scope;
}

export function trackNetworkPromise<T>(
  promise: Promise<T>,
  record: RequestRecord = {
    errors: new Set(),
    groups: new Set(),
    independentConsumer: false,
  }
): Promise<T> {
  if (promises.has(promise)) return promise;
  promises.add(promise);
  const then = promise.then.bind(promise);
  let branches = 0;
  let aggregateSubscriptions = 0;
  const recordError = (error: unknown): void => {
    if (typeof error !== 'object' || error === null) return;
    record.errors.add(error);
    let requests = errorRequests.get(error);
    if (!requests) {
      requests = new Set();
      errorRequests.set(error, requests);
    }
    requests.add(record);
    for (const group of record.groups) recordGroupError(group, error);
  };
  const trackedThen: Promise<T>['then'] = (onFulfilled, onRejected) => {
    if (nativeCallback(onFulfilled) && nativeCallback(onRejected)) {
      if (branches > 0) record.independentConsumer = true;
      aggregateSubscriptions++;
      joinGroup(record, onFulfilled, onRejected);
    } else if (++branches > 1 || aggregateSubscriptions > 0) {
      record.independentConsumer = true;
    }
    return trackNetworkPromise(
      then(onFulfilled, (error: unknown) => {
        recordError(error);
        if (onRejected) return onRejected(error);
        throw error;
      }),
      record
    );
  };
  return Object.defineProperty(promise, 'then', {
    value: trackedThen,
    configurable: true,
    writable: true,
  });
}
